import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Table } from 'primeng/table';
import { Menu } from 'primeng/menu';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseServices } from '../../kuba.services';
import { SdsApiService } from '../services/sds-api.service';
import { KubaExportService } from '../../shared/kuba-exports/kuba-export.service';
import {
  SdsExportColumn as ExportColumn,
  SdsExportFormat as ExportFormat,
  SdsExportService
} from '../services/sds-export.service';
import {
  SdsExposureAccessLogRow,
  SdsExposureRegisterRow,
  SdsHazardCategory
} from '../models/sds.model';

interface HazardCategoryOption {
  value: SdsHazardCategory | null;
  labelKey: string;
}

interface RetentionStatus {
  key: 'ongoing' | 'active' | 'expiring' | 'expired';
  labelKey: string;
  cssClass: string;
}

/**
 * Cross-document Exposure Register grid.
 *
 * Reads only — additions and routine edits happen from the document detail page
 * via the shared exposure dialog. Compliance-sensitive edits (legal hold, NIN,
 * deletion permissions) are still document-side only by policy.
 *
 * The grid is the canonical "audit view" for HSE managers and Arbeidstilsynet
 * inspectors: every employee, every chemical, every retention deadline,
 * masked NIN with reveal-on-demand, and CSV export — all in one place.
 */
@Component({
  selector: 'kuba-sds-exposure-register',
  templateUrl: './sds-exposure-register.component.html',
  styleUrls: ['./sds-exposure-register.component.css', './sds-theme.css'],
  providers: [KubaExportService, SdsExportService],
  standalone: false
})
export class SdsExposureRegisterComponent implements OnInit {
  @ViewChild('registerTable', { static: false }) registerTable: Table | null = null;
  @ViewChild('logTable', { static: false }) logTable: Table | null = null;

  rows: SdsExposureRegisterRow[] = [];
  loading = false;
  error: string | null = null;

  // ----- Tabs -----
  activeTab: 'register' | 'log' = 'register';

  /** Whether the current user may see exposure PII — gates the Access log tab. */
  canAccessPii = false;

  // ----- Access-log report tab -----
  accessLogRows: SdsExposureAccessLogRow[] = [];
  accessLogLoading = false;
  accessLogError: string | null = null;
  accessLogLoaded = false;

  hazardCategoryOptions: HazardCategoryOption[] = [
    { value: null, labelKey: 'SDS_HAZARD_CATEGORY_ANY' },
    { value: SdsHazardCategory.None, labelKey: 'SDS_HAZARD_CATEGORY_NONE' },
    { value: SdsHazardCategory.Asbestos, labelKey: 'SDS_HAZARD_CATEGORY_ASBESTOS' },
    { value: SdsHazardCategory.Carcinogenic, labelKey: 'SDS_HAZARD_CATEGORY_CARCINOGENIC' },
    { value: SdsHazardCategory.Mutagenic, labelKey: 'SDS_HAZARD_CATEGORY_MUTAGENIC' },
    { value: SdsHazardCategory.Reprotoxic, labelKey: 'SDS_HAZARD_CATEGORY_REPROTOXIC' },
    { value: SdsHazardCategory.Lead, labelKey: 'SDS_HAZARD_CATEGORY_LEAD' },
    { value: SdsHazardCategory.BiologicalAgent, labelKey: 'SDS_HAZARD_CATEGORY_BIOLOGICAL_AGENT' },
    { value: SdsHazardCategory.IonisingRadiation, labelKey: 'SDS_HAZARD_CATEGORY_IONISING_RADIATION' },
    { value: SdsHazardCategory.OtherRegulated, labelKey: 'SDS_HAZARD_CATEGORY_OTHER_REGULATED' }
  ];

  // ----- Reveal dialog state (mirrors the document-detail behaviour). -----
  /** Minimum reason length, must match the server-side validator. */
  readonly minReasonLength = 10;

  revealDialogVisible = false;
  revealDialogLoading = false;
  revealDialogReason = '';
  revealDialogRow: SdsExposureRegisterRow | null = null;
  revealedNin: string | null = null;
  revealError: string | null = null;

  /** Live char counter for the reason textarea — drives the hint colour. */
  get reasonLength(): number {
    return (this.revealDialogReason || '').trim().length;
  }

  /** True once the user has typed enough to actually submit the reveal. */
  get reasonMeetsMinimum(): boolean {
    return this.reasonLength >= this.minReasonLength;
  }

  // ----- Export state -----
  /** True while an export request/render is in flight (disables export buttons). */
  exportLoading = false;
  /** Model + menu for the consolidated Export dropdown. */
  exportMenuItems: MenuItem[] = [];
  @ViewChild('exportMenu', { static: false }) exportMenu?: Menu;
  /** Reason prompt shown before a Register export (sensitive PII, audited). */
  exportReasonDialogVisible = false;
  exportReasonText = '';
  exportReasonError: string | null = null;
  private pendingExportFormat: ExportFormat = 'XLSX';

  constructor(
    private readonly sdsApi: SdsApiService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly translate: TranslateService,
    private readonly sdsExport: SdsExportService
  ) {}

  ngOnInit(): void {
    this.loadRegister();

    // The Access log tab is only shown to users who may see exposure PII
    // (Admin/Portal, or Editors on the business allowlist).
    this.sdsApi.getMyExposureAccess(BaseServices.BusinessId).subscribe({
      next:access => {
        this.canAccessPii = !!access?.CanAccessEmployeeExposureData;
      },
      error: () => {
        this.canAccessPii = false;
      }
    });
  }

  selectTab(tab: 'register' | 'log'): void {
    if (tab === 'log' && !this.canAccessPii) {
      return;
    }
    this.activeTab = tab;
    if (tab === 'log' && !this.accessLogLoaded) {
      this.loadAccessLog();
    }
  }

  loadAccessLog(): void {
    this.accessLogLoading = true;
    this.accessLogError = null;

    this.sdsApi.getExposureAccessLog(BaseServices.BusinessId).subscribe({
      next:rows => {
        this.accessLogRows = rows;
        this.accessLogLoaded = true;
        this.accessLogLoading = false;
      },
      error: () => {
        this.accessLogError = this.translate.instant('SDS_EXPOSURE_ACCESS_LOG_LOAD_FAILED');
        this.accessLogLoading = false;
      }
    });
  }

  /** Maps the SDS_ExposureAccessKind byte to a translation key. */
  accessKindLabelKey(kind: number): string {
    switch (kind) {
      case 1: return 'SDS_ACCESS_KIND_LISTVIEW';
      case 2: return 'SDS_ACCESS_KIND_DETAILVIEW';
      case 3: return 'SDS_ACCESS_KIND_REVEAL';
      case 4: return 'SDS_ACCESS_KIND_EXPORT';
      case 5: return 'SDS_ACCESS_KIND_EDIT';
      case 6: return 'SDS_ACCESS_KIND_DELETE';
      case 7: return 'SDS_ACCESS_KIND_LEGALHOLD';
      case 8: return 'SDS_ACCESS_KIND_REVEALFAILED';
      default: return 'SDS_ACCESS_KIND_UNKNOWN';
    }
  }

  /** Reveal-related kinds get a highlighted badge in the report. */
  isRevealKind(kind: number): boolean {
    return kind === 3 || kind === 8;
  }

  loadRegister(): void {
    this.loading = true;
    this.error = null;
    const businessId = BaseServices.BusinessId;

    this.sdsApi.getExposureRegister(businessId).subscribe({
      next:rows => {
        this.rows = rows;
        this.loading = false;
      },
      error: () => {
        this.error = this.translate.instant('SDS_EXPOSURE_REGISTER_LOAD_FAILED');
        this.loading = false;
      }
    });
  }

  refresh(): void {
    if (this.activeTab === 'log') {
      this.loadAccessLog();
    } else {
      this.loadRegister();
    }
  }

  /** True when the current tab has data available to export. */
  get canExport(): boolean {
    if (this.exportLoading) {
      return false;
    }
    return this.activeTab === 'log'
      ? !this.accessLogLoading && this.accessLogRows.length > 0
      : !this.loading && this.rows.length > 0;
  }

  /** Opens the consolidated Export dropdown (Excel / PDF / CSV). */
  openExportMenu(event: Event): void {
    this.exportMenuItems = [
      { label: 'Excel', icon: 'pi pi-file-excel', command: () => this.dispatchExport('XLSX') },
      { label: 'PDF', icon: 'pi pi-file-pdf', command: () => this.dispatchExport('PDF') },
      { label: 'CSV', icon: 'pi pi-file', command: () => this.dispatchExport('CSV') }
    ];
    this.exportMenu?.toggle(event);
  }

  /** Runs the export for the active tab in the chosen format. */
  private dispatchExport(format: ExportFormat): void {
    if (this.activeTab === 'log') {
      this.exportAccessLog(format);
    } else {
      this.exportRegister(format);
    }
  }

  /**
   * Export the Access-log tab (XLSX/PDF/CSV). The access log is itself the audit
   * trail, so no reason prompt and no extra audit entry are created. Reflects the
   * table's current filter/sort via the live filtered view.
   */
  exportAccessLog(format: ExportFormat): void {
    if (!this.accessLogRows.length || this.exportLoading) {
      return;
    }

    const { columnNames, data } = this.buildAccessLogExport(this.currentAccessLogView());
    const header = this.translate.instant('SDS_EXPOSURE_ACCESS_LOG_TAB');

    this.exportLoading = true;
    this.sdsExport.run(format, data, columnNames, header, 'exposure-access-log').subscribe({
      next: () => {
        this.exportLoading = false;
      },
      error: () => {
        this.exportLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'SDS',
          detail: this.translate.instant('SDS_EXPOSURE_EXPORT_FAILED')
        });
      }
    });
  }

  /** Per-row hazard category badge label key. Falls back to 'None' for safety. */
  hazardLabelKey(category: SdsHazardCategory | null | undefined): string {
    const option = this.hazardCategoryOptions.find(opt => opt.value === category);
    return option?.labelKey ?? 'SDS_HAZARD_CATEGORY_NONE';
  }

  /**
   * Compact retention-status indicator for the grid. Drives the badge colour:
   *  - ongoing (no end date): blue/neutral
   *  - active (expiry > 1y away): green
   *  - expiring (within 1y): amber
   *  - expired (past): red — but kept in the register due to legal-hold
   */
  retentionStatus(row: SdsExposureRegisterRow): RetentionStatus {
    if (!row.RetentionExpiryDate) {
      return { key: 'ongoing', labelKey: 'SDS_RETENTION_ONGOING', cssClass: 'badge-ongoing' };
    }

    const expiry = new Date(row.RetentionExpiryDate as string).getTime();
    const now = Date.now();
    const oneYear = 365 * 24 * 60 * 60 * 1000;

    if (expiry < now) {
      return { key: 'expired', labelKey: 'SDS_RETENTION_EXPIRED', cssClass: 'badge-expired' };
    }
    if (expiry - now < oneYear) {
      return { key: 'expiring', labelKey: 'SDS_RETENTION_EXPIRING', cssClass: 'badge-expiring' };
    }
    return { key: 'active', labelKey: 'SDS_RETENTION_ACTIVE', cssClass: 'badge-active' };
  }

  displayName(row: SdsExposureRegisterRow): string {
    const name = `${row.FirstName ?? ''} ${row.LastName ?? ''}`.trim();
    return name || this.translate.instant('SDS_EMPLOYEE_UNNAMED');
  }

  /**
   * Deep-link to the parent SDS document so edits (incl. compliance fields) happen there.
   * Routing is **relative** because the SDS module is mounted under different prefixes
   * for admin / portal / business — absolute paths only resolve under one of them.
   */
  openDocument(row: SdsExposureRegisterRow): void {
    this.router.navigate(['../detail', row.DocumentId], {
      relativeTo: this.route,
      fragment: `exposure-${row.ExposureId}`
    });
  }

  // ----- Reveal dialog -----

  startReveal(row: SdsExposureRegisterRow): void {
    if (!row.NationalIdentityNumberMasked) {
      return; // No NIN recorded, or user not on the allowlist.
    }
    this.revealDialogRow = row;
    this.revealDialogReason = '';
    this.revealedNin = null;
    this.revealError = null;
    this.revealDialogVisible = true;
  }

  confirmReveal(): void {
    const row = this.revealDialogRow;
    const reason = (this.revealDialogReason || '').trim();

    // Same minimum the backend enforces. Show the message inline (instead of
    // silently keeping the button disabled) so the requirement is obvious if
    // the user clicks before reading the hint.
    if (!row || reason.length < this.minReasonLength) {
      this.revealError = this.translate.instant('SDS_REVEAL_NIN_REASON_TOO_SHORT');
      return;
    }

    this.revealDialogLoading = true;
    this.revealError = null;

    this.sdsApi.revealExposureNationalIdentityNumber(row.ExposureId, reason).subscribe({
      next:response => {
        this.revealedNin = response.NationalIdentityNumber || null;
        this.revealDialogLoading = false;
      },
      error: error => {
        this.revealError = this.getRevealErrorMessage(error);
        this.revealDialogLoading = false;
      }
    });
  }

  private getRevealErrorMessage(error: any): string {
    if (error?.error?.Code === 'SDS_NIN_KEY_UNAVAILABLE') {
      return this.translate.instant('SDS_REVEAL_NIN_KEY_UNAVAILABLE');
    }

    return ( error?.error?.Message
      || error?.error?.message
      || error?.error?.detail
      || this.translate.instant('SDS_REVEAL_NIN_DENIED'));
  }

  closeRevealDialog(): void {
    this.revealDialogVisible = false;
    this.revealDialogRow = null;
    this.revealedNin = null;
    this.revealDialogReason = '';
    this.revealError = null;
    this.revealDialogLoading = false;
  }

  /** System-generated access reasons → translation keys (user-typed reasons pass through unchanged). */
  private readonly accessReasonKeys: Record<string, string> = {
    'register-view': 'SDS_ACCESS_REASON_REGISTER_VIEW',
    'register-view-no-pii': 'SDS_ACCESS_REASON_REGISTER_VIEW_MASKED'
  };

  /** Humanises a logged access reason: known system codes are translated; free text is shown as-is. */
  displayAccessReason(reason: string | null | undefined): string {
    if (!reason) {
      return '';
    }
    const key = this.accessReasonKeys[reason];
    return key ? this.translate.instant(key) : reason;
  }

  /**
   * Formats a stored UTC access-log timestamp in Norwegian local time
   * (Europe/Oslo, DST-aware — GMT+1 in winter, GMT+2 in summer).
   */
  formatLogTime(value: string | Date | null | undefined): string {
    if (!value) {
      return '—';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Oslo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  }

  // ----- Register export (audited, unmasked person number) -----

  /**
   * Begin a Register export (XLSX/PDF/CSV). All register exports carry the
   * unmasked person number, so they always go through the reason prompt and the
   * audited backend endpoint, regardless of format.
   */
  exportRegister(format: ExportFormat): void {
    if (!this.rows.length || this.exportLoading) {
      return;
    }
    this.pendingExportFormat = format;
    this.exportReasonText = '';
    this.exportReasonError = null;
    this.exportReasonDialogVisible = true;
  }

  /** Confirms the reason, fetches the unmasked rows (audited), then renders the file. */
  confirmRegisterExport(): void {
    const reason = (this.exportReasonText || '').trim();
    if (reason.length < this.minReasonLength) {
      this.exportReasonError = this.translate.instant('SDS_REVEAL_NIN_REASON_TOO_SHORT');
      return;
    }

    this.exportLoading = true;
    this.exportReasonError = null;
    const format = this.pendingExportFormat;

    this.sdsApi.exportExposureRegisterRows(BaseServices.BusinessId, reason).subscribe({
      next:unmaskedRows => {
        const ninByExposureId = new Map<number, string | null>(
          (unmaskedRows || []).map(r => [r.ExposureId, r.NationalIdentityNumber ?? null])
        );
        const { columnNames, data } = this.buildRegisterExport(
          this.currentRegisterView(),
          ninByExposureId
        );
        const header = this.translate.instant('SDS_EXPOSURE_REGISTER');

        this.sdsExport.run(format, data, columnNames, header, 'exposure-register').subscribe({
          next: () => {
            this.exportLoading = false;
            this.exportReasonDialogVisible = false;
            this.messageService.add({
              severity: 'success',
              summary: 'SDS',
              detail: this.translate.instant('SDS_EXPOSURE_EXPORT_SUCCESS')
            });
          },
          error: () => {
            this.exportLoading = false;
            this.exportReasonError = this.translate.instant('SDS_EXPOSURE_EXPORT_FAILED');
          }
        });
      },
      error:err => {
        this.exportLoading = false;
        this.exportReasonError = err?.status === 403
          ? this.translate.instant('SDS_REVEAL_NIN_DENIED')
          : this.translate.instant('SDS_EXPOSURE_EXPORT_FAILED');
      }
    });
  }

  closeExportDialog(): void {
    if (this.exportLoading) {
      return;
    }
    this.exportReasonDialogVisible = false;
    this.exportReasonText = '';
    this.exportReasonError = null;
  }

  /** Sets the global filter on the underlying p-table from the search input. */
  applyGlobalFilter(value: string): void {
    if (this.registerTable) {
      this.registerTable.filterGlobal(value, 'contains');
    }
  }

  // ----- Export helpers -----

  /** Current Register view, honouring the table's active filter/sort. */
  private currentRegisterView(): SdsExposureRegisterRow[] {
    return (
      (this.registerTable?.filteredValue as SdsExposureRegisterRow[]) ?? this.rows
    );
  }

  /** Current Access-log view, honouring the table's active filter/sort. */
  private currentAccessLogView(): SdsExposureAccessLogRow[] {
    return (
      (this.logTable?.filteredValue as SdsExposureAccessLogRow[]) ?? this.accessLogRows
    );
  }

  /** Builds localized columns + flat records for the Register export. */
  private buildRegisterExport(
    rows: SdsExposureRegisterRow[],
    ninByExposureId: Map<number, string | null>
  ): { columnNames: ExportColumn[]; data: any[] } {
    const t = (key: string) => this.translate.instant(key);

    const columnNames: ExportColumn[] = [
      { title: t('SDS_EMPLOYEE'), dataKey: 'Employee' },
      { title: t('SDS_NIN'), dataKey: 'PersonNumber' },
      { title: t('SDS_PRODUCT_NAME'), dataKey: 'Product' },
      { title: t('SDS_SUPPLIER'), dataKey: 'Supplier' },
      { title: t('SDS_CAS_NUMBER'), dataKey: 'CAS' },
      { title: t('SDS_HAZARD_CATEGORY'), dataKey: 'HazardCategory' },
      { title: t('SDS_EXPOSURE_START_DATE'), dataKey: 'ExposureStart' },
      { title: t('SDS_EXPOSURE_END_DATE'), dataKey: 'ExposureEnd' },
      { title: t('SDS_RETENTION'), dataKey: 'RetentionExpiry' },
      { title: t('SDS_MEDICAL_EXAM_DATE'), dataKey: 'MedicalExamDate' },
      { title: t('SDS_MEDICAL_EXAM_DOCTOR_NAME'), dataKey: 'MedicalExamDoctor' }
    ];

    const data = rows.map(row => ({
      Employee: this.displayName(row),
      // Unmasked person number from the audited endpoint; fall back to masked if the
      // record's key was unavailable server-side.
      PersonNumber: ninByExposureId.get(row.ExposureId) || row.NationalIdentityNumberMasked || '',
      Product: row.ProductName ?? '',
      Supplier: row.Supplier ?? '',
      CAS: row.CASNumber ?? '',
      HazardCategory: t(this.hazardLabelKey(row.HazardCategory)),
      ExposureStart: this.fmtDate(row.ExposureStartDate),
      ExposureEnd: this.fmtDate(row.ExposureEndDate),
      RetentionExpiry: this.fmtDate(row.RetentionExpiryDate),
      MedicalExamDate: this.fmtDate(row.MedicalExamDate),
      MedicalExamDoctor: row.MedicalExamDoctorName ?? ''
    }));

    return { columnNames, data };
  }

  /** Builds localized columns + flat records for the Access-log export. */
  private buildAccessLogExport(
    rows: SdsExposureAccessLogRow[]
  ): { columnNames: ExportColumn[]; data: any[]; } {
    const t = (key: string) => this.translate.instant(key);

    const columnNames: ExportColumn[] = [
      { title: t('SDS_ACCESS_LOG_WHEN'), dataKey: 'When' },
      { title: t('SDS_ACCESS_LOG_BY'), dataKey: 'By' },
      { title: t('SDS_EMPLOYEE'), dataKey: 'Employee' },
      { title: t('SDS_PRODUCT_NAME'), dataKey: 'Product' },
      { title: t('SDS_ACCESS_LOG_REASON'), dataKey: 'Reason' },
      { title: t('SDS_ACCESS_LOG_IP'), dataKey: 'IP' }
    ];

    const data = rows.map(row => ({
      When: this.fmtDateTime(row.AccessedOnUtc),
      By: row.AccessedByName ?? String(row.AccessedByUserId),
      Employee: row.EmployeeName ?? '',
      Product: row.ProductName ?? '',
      Reason: row.Reason ?? '',
      IP: row.IpAddress ?? ''
    }));

    return { columnNames, data };
  }

  /**
   * Renders an export. CSV is built client-side; XLSX/PDF go through the shared
   * Kuba export service (business logo + standard layout, rendered server-side).
   */
  private fmtDate(value: string | Date | null | undefined): string {
    return this.sdsExport.formatDate(value);
  }

  private fmtDateTime(value: string | Date | null | undefined): string {
    if (!value) return '';
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${this.fmtDate(d)} ${hh}:${min}`;
  }
}
